import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Hr from "@saleor/components/Hr";
import {
  validateFileSize,
  validateImageDimensions,
  validateMimeTypes
} from "@saleor/coupons/validators";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { commonMessages } from "@saleor/intl";
import { CouponCollectionFormData } from "@saleor/utils/validators/couponCollection";
import classNames from "classnames";
import React, { useRef, useState } from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    card: {
      height: "400px",
      marginBottom: "20px"
    },
    descriptionLabel: {
      margin: "5px 0px 5px 0px"
    },
    fileField: {
      display: "none"
    },
    fileUploadField: {
      padding: "0!important"
    },
    guidelines: {
      fontWeight: "bold"
    },
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      position: "relative",
      width: "100%"
    },
    imageGridContainer: {
      justifyContent: "center"
    },
    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: "100%",
      left: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageOverlayShow: {
      "& imageOverlay": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      }
    },
    imageOverlayToolbar: {
      display: "flex",
      position: "absolute",
      right: 0,
      top: 0
    },
    imageflex: {},
    info: {
      color: "#0937f4",
      fontSize: 17,
      fontWeight: "bold"
    },
    infoGuidelines: {
      fontSize: 13,
      marginLeft: "10px",
      padding: "5px 0 0 0"
    },
    infoOutlinedIcon: {
      marginLeft: theme.spacing(1)
    },
    infoPanel: {
      padding: "20px 40px 0 40px"
    },
    infoPanelTitle: {
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(4),
      justifyContent: "flex-start",
      marginBottom: 10
    },
    mainPromoTitleContainer: {
      alignItems: "center",
      display: "flex"
    },
    photosIcon: {
      height: "64px",
      margin: "0 auto",
      width: "64px"
    },
    uploadButton: {
      display: "block"
    },
    uploadRoot: {
      fontSize: 12,
      fontWeight: 600,
      padding: "15px",
      textAlign: "center"
    }
  }),
  { name: "CouponCollectionImage" }
);

interface CouponCollectionImageProps {
  data: CouponCollectionFormData;
  loading?: boolean;
  onChange: FormChange;
  errors: any;
  disabled?: boolean;
  onCouponCollectionImageUpload?: (file: File) => void;
}

export const CouponCollectionImage: React.FC<CouponCollectionImageProps> = props => {
  const {
    data,
    loading,
    onChange,
    onCouponCollectionImageUpload,
    disabled
  } = props;
  const notify = useNotifier();
  const classes = useStyles(props);
  const intl = useIntl();
  const upload = useRef(null);
  const [couponCollectionUpload, setCouponCollectionUpload] = useState(null);
  const [couponCollectionImage, setCouponCollectionImage] = useState(null);

  const checkFile = async e => {
    const file = e[0];
    if (!file) {
      return false;
    }
    try {
      await validateImageDimensions(file, {
        height: { max: 640, min: 440 },
        width: { max: 1080, min: 720 }
      });
      await validateFileSize(file);
      await validateMimeTypes(file);

      setCouponCollectionUpload(e);
      onCouponCollectionImageUpload(e);
      onChange({
        target: {
          name: "bannerImage",
          value: file
        }
      });
    } catch (error) {
      notify({
        status: "error",
        text: error,
        title: "Can't Upload."
      });
    }
  };

  const cardTitle = (
    <div className={classes.infoPanelTitle}>
      <strong>
        {intl.formatMessage({
          defaultMessage: "Collection Banner",
          description: "Coupon Collection Banner"
        })}
      </strong>
    </div>
  );

  React.useEffect(() => {
    if (couponCollectionUpload) {
      setCouponCollectionImage(URL.createObjectURL(couponCollectionUpload[0]));
      onCouponCollectionImageUpload(couponCollectionUpload[0]);
    }
  }, [couponCollectionUpload]);

  return (
    <Card className={classes.card}>
      <CardTitle
        title={cardTitle}
        toolbar={
          <>
            <Button
              onClick={() => upload.current.click()}
              disabled={loading || disabled}
              variant="text"
              id="couponCollectionUploadBtn"
              color="primary"
              data-test="button-upload-image"
              className={classes.uploadButton}
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="couponCollectionUploadinput"
              onChange={e => checkFile(e.target.files)}
              type="file"
              ref={upload}
              accept="image/jpeg, image/jpg, image/png"
            />
          </>
        }
      />
      <div
        className={classNames(classes.imageGridContainer, {
          [classes.imageflex]: couponCollectionImage || data.bannerImage
        })}
      >
        <CardContent>
          <Dropzone onDrop={checkFile}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent className={classes.fileUploadField}>
                  <input
                    {...getInputProps()}
                    id="couponCollectionUploadDropzone"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                    disabled={disabled}
                  />
                  {couponCollectionImage || data.bannerImage ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                upload.current.value = null;
                                setCouponCollectionImage(null);
                                onCouponCollectionImageUpload(null);
                                onChange({
                                  target: {
                                    name: "bannerImage",
                                    value: null
                                  }
                                });
                              }}
                              disabled={disabled}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        className={classes.image}
                        src={
                          couponCollectionUpload && couponCollectionImage
                            ? couponCollectionImage
                            : data.bannerImage
                        }
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>
        <Hr />
        <div className={classes.infoPanel}>
          <Grid>
            <div className={classes.infoPanelTitle}>
              <InfoOutlined color="primary" />
              <Typography className={classes.info}>
                Upload Guidelines:
              </Typography>
            </div>
          </Grid>
          <div>
            <span className={classes.infoGuidelines}>
              {" "}
              1. Image Width Dimension:{" "}
              <span className={classes.guidelines}>720px to 1080px.</span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              2. Image Height Dimension:{" "}
              <span className={classes.guidelines}>440px to 640px.</span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              3. Image file should{" "}
              <span className={classes.guidelines}>
                not be greater than 2MB.
              </span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              4. Only{" "}
              <span className={classes.guidelines}>
                JPG, JPEG and PNG files
              </span>{" "}
              are acceptable.
            </span>
          </div>
          <CardContent></CardContent>
        </div>
      </div>
    </Card>
  );
};

export default CouponCollectionImage;
